import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { roleGuard } from './core/guards/role.guard';
import { adminGuard } from './core/auth/admin.guard';

export const routes: Routes = [
  // Default landing: redirect to welcome; guestGuard will automatically redirect
  // authenticated returning users directly to their shell.
  { path: '', pathMatch: 'full', redirectTo: 'welcome' },

  // Public auth screens protected by guestGuard: authenticated users bounce to their shell
  {
    path: 'welcome',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/welcome.page').then((m) => m.WelcomePage),
  },
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/login.page').then((m) => m.LoginPage),
  },
  {
    path: 'cadastro-empresa',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/cadastro-empresa.page').then((m) => m.CadastroEmpresaPage),
  },
  {
    path: 'cadastro-profissional',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/cadastro-profissional.page').then((m) => m.CadastroProfissionalPage),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./features/auth/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  // Phase 16 v1.3 — SOCIAL-03. OAuth callback target. Public (no authGuard)
  // because the user does not yet have a session when this page loads — the
  // page itself hydrates AuthStore from the token query param.
  {
    path: 'auth/oauth-success',
    loadComponent: () =>
      import('./features/auth/oauth-callback.page').then((m) => m.OAuthCallbackPage),
  },
  // Phase 16 v1.3 — SOCIAL-03. Complete-signup finalize page for OAuth-only
  // users in pendente_completar state. Requires a JWT (so authGuard passes
  // once oauth-callback.page hydrated the session) but NOT a role
  // (roleGuard would reject role=null users — explicitly omitted here).
  {
    path: 'complete-cadastro',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/auth/complete-cadastro.page').then((m) => m.CompleteCadastroPage),
  },
  {
    path: 'aceitar-convite',
    loadComponent: () =>
      import('./features/auth/aceitar-convite.page').then((m) => m.AceitarConvitePage),
  },
  {
    path: 'privacidade',
    loadComponent: () =>
      import('./features/legal/privacidade.page').then((m) => m.PrivacidadePage),
  },
  {
    path: 'termos',
    loadComponent: () => import('./features/legal/termos.page').then((m) => m.TermosPage),
  },
  {
    path: 'dpo',
    loadComponent: () => import('./features/legal/dpo.page').then((m) => m.DpoPage),
  },

  // Phase 27 v1.6 — REQ-SHARE-01 (D-06, D-19). Public landing route — NO auth-guard.
  // Renders EventoPublicDto (LGPD whitelist). Anonymous access by design.
  {
    path: 'e/:slug',
    loadComponent: () =>
      import('./features/public/evento-publico.page').then(
        (m) => m.EventoPublicoPage,
      ),
  },

  // Protected shells
  {
    path: 'empresa',
    canActivate: [authGuard, roleGuard('EMPRESA')],
    loadComponent: () =>
      import('./shells/empresa-shell/empresa-shell.component').then(
        (m) => m.EmpresaShellComponent,
      ),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/empresa/dashboard/dashboard.page').then(
            (m) => m.DashboardPage,
          ),
      },
      {
        path: 'eventos',
        loadComponent: () =>
          import('./features/empresa/eventos/meus-eventos.page').then(
            (m) => m.MeusEventosPage,
          ),
      },
      {
        path: 'eventos/novo',
        loadComponent: () =>
          import('./features/empresa/eventos/criar-evento.page').then(
            (m) => m.CriarEventoPage,
          ),
      },
      {
        path: 'eventos/:id',
        loadComponent: () =>
          import('./features/empresa/eventos/detalhe-evento.page').then(
            (m) => m.DetalheEventoPage,
          ),
      },
      {
        path: 'eventos/:id/editar',
        loadComponent: () =>
          import('./features/empresa/eventos/editar-evento.page').then(
            (m) => m.EditarEventoPage,
          ),
      },
      {
        // Phase 19 v1.4 — PIX-01. Empresa checkout (PIX QR + 5s polling).
        path: 'eventos/:id/pagar',
        loadComponent: () =>
          import('./features/empresa/eventos/checkout.page').then(
            (m) => m.CheckoutPixPage,
          ),
      },
      {
        path: 'eventos/:id/candidatos',
        loadComponent: () =>
          import('./features/empresa/candidatos/candidatos.page').then(
            (m) => m.CandidatosPage,
          ),
      },
      {
        // Phase 12 v1.2 — AUDIT-CAND-02. Empresa-side single-candidato view
        // with Detalhes + Histórico tabs. `:eventoId` (not `:id`) makes the
        // back-button defaultHref reconstruction unambiguous.
        path: 'eventos/:eventoId/candidatos/:candidaturaId',
        loadComponent: () =>
          import(
            './features/empresa/candidatos/detalhe-candidato.page'
          ).then((m) => m.DetalheCandidatoPage),
      },
      {
        path: 'equipe',
        loadComponent: () =>
          import('./features/empresa/equipe/equipe.page').then((m) => m.EquipePage),
      },
      {
        path: 'equipe/convidar',
        loadComponent: () =>
          import('./features/empresa/equipe/convidar-membro.page').then(
            (m) => m.ConvidarMembroPage,
          ),
      },
      {
        // Phase 26 — REQ-CATALOGO-01. Empresa Catálogo + CONVOCAR routes.
        path: 'equipe-profissionais',
        loadChildren: () =>
          import(
            './features/empresa/equipe-profissionais/equipe-profissionais.routes'
          ).then((m) => m.EQUIPE_PROFISSIONAIS_ROUTES),
      },
      {
        path: 'relatorios',
        loadComponent: () =>
          import('./features/empresa/relatorios/relatorios.page').then(
            (m) => m.RelatoriosPage,
          ),
      },
      {
        path: 'carteira',
        loadComponent: () =>
          import('./features/empresa/carteira/carteira-empresa.page').then(
            (m) => m.CarteiraEmpresaPage,
          ),
      },
      // Phase 27 v1.6 — REQ-WALLET-01 (D-20). Depositar PIX page.
      {
        path: 'carteira/depositar',
        loadComponent: () =>
          import('./features/empresa/carteira/depositar.page').then(
            (m) => m.DepositarPage,
          ),
      },
      // Saque via PIX para Empresa
      {
        path: 'carteira/sacar',
        loadComponent: () =>
          import('./features/empresa/carteira/sacar.page').then(
            (m) => m.EmpresaSacarPage,
          ),
      },
      // Phase 28 v1.6 — REQ-EXTRATO-01 (D-02, D-03). Empresa extrato + recibo.
      // Auth + role guard inherited from parent /empresa route.
      {
        path: 'carteira/extrato',
        loadComponent: () =>
          import('./features/empresa/carteira/extrato.page').then(
            (m) => m.EmpresaExtratoPage,
          ),
      },
      {
        path: 'carteira/extrato/:id',
        loadComponent: () =>
          import('./features/empresa/carteira/recibo.page').then(
            (m) => m.EmpresaReciboPage,
          ),
      },
    ],
  },
  {
    path: 'profissional',
    canActivate: [authGuard, roleGuard('PROFISSIONAL')],
    loadComponent: () =>
      import('./shells/profissional-shell/profissional-shell.component').then(
        (m) => m.ProfissionalShellComponent,
      ),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'feed' },
      {
        path: 'feed',
        loadComponent: () =>
          import('./features/profissional/feed/feed.page').then((m) => m.FeedPage),
      },
      {
        path: 'eventos',
        loadComponent: () =>
          import('./features/profissional/feed/feed.page').then((m) => m.FeedPage),
      },
      {
        path: 'eventos/:id',
        loadComponent: () =>
          import('./features/profissional/eventos/detalhe-evento-publico.page').then(
            (m) => m.DetalheEventoPublicoPage,
          ),
      },
      {
        path: 'meus-eventos',
        loadComponent: () =>
          import('./features/profissional/candidaturas/meus-eventos.page').then(
            (m) => m.MeusEventosProfPage,
          ),
      },
      {
        path: 'meus-eventos/:id',
        loadComponent: () =>
          import('./features/profissional/candidaturas/detalhe-candidatura.page').then(
            (m) => m.DetalheCandidaturaPage,
          ),
      },
      {
        path: 'carteira',
        loadComponent: () =>
          import('./features/profissional/carteira/carteira-prof.page').then(
            (m) => m.CarteiraProfPage,
          ),
      },
      {
        // Phase 19 v1.4 — PIX-03. Profissional saca via PIX.
        path: 'carteira/sacar',
        loadComponent: () =>
          import('./features/profissional/carteira/sacar.page').then(
            (m) => m.SacarPage,
          ),
      },
      {
        // Phase 24 v1.6 — REQ-PIX-01 / D-09. Default PIX key CRUD.
        path: 'carteira/dados-bancarios',
        loadComponent: () =>
          import('./features/profissional/carteira/dados-bancarios.page').then(
            (m) => m.DadosBancariosPage,
          ),
      },
      // Phase 28 v1.6 — REQ-EXTRATO-01 (D-02, D-03). Prof extrato + recibo.
      // Auth + role guard inherited from parent /profissional route.
      {
        path: 'carteira/extrato',
        loadComponent: () =>
          import('./features/profissional/carteira/extrato.page').then(
            (m) => m.ProfExtratoPage,
          ),
      },
      {
        path: 'carteira/extrato/:id',
        loadComponent: () =>
          import('./features/profissional/carteira/recibo.page').then(
            (m) => m.ProfReciboPage,
          ),
      },
      {
        path: 'perfil',
        loadComponent: () =>
          import('./features/profissional/perfil/perfil-placeholder.page').then(
            (m) => m.PerfilPlaceholderPage,
          ),
      },
      {
        // Phase 18 v1.4 — KYC-01. Profissional uploads 3 photos (frente/
        // verso/selfie). Auth + role guarded via the parent shell route.
        path: 'perfil/kyc',
        loadComponent: () =>
          import('./features/profissional/perfil/kyc.page').then(
            (m) => m.KycPage,
          ),
      },
      {
        // Phase 17 v1.3 — FILA-03. Profissional self-view of every pendente
        // ABERTO candidatura with posicao + totalNaFila per evento. Reached
        // from the perfil widget; not surfaced in the shell tab bar.
        path: 'fila',
        loadComponent: () =>
          import('./features/profissional/fila/fila.page').then((m) => m.FilaPage),
      },
    ],
  },

  // Phase 15 (PUSH-03) + Phase 20 v1.4 (NOTIF-01) — settings shared between
  // empresa + profissional roles. No roleGuard: any authenticated user can
  // manage notification preferences. Plan 20-02 upgraded the page from a
  // single master push toggle to a master toggle + per-kind push/email
  // matrix (PATCH /me/notification-prefs).
  {
    path: 'configuracoes/notificacoes',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/settings/notificacoes.page').then(
        (m) => m.NotificacoesPage,
      ),
  },
  // Phase 20 v1.4 (LINK-01) — Account linking page. Lists Google + Apple with
  // current connection status. Native Apple link is fully wired on iOS
  // Capacitor; other platforms show a "Conectar disponível no app iOS" hint.
  {
    path: 'configuracoes/contas-conectadas',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/settings/contas-conectadas.page').then(
        (m) => m.ContasConectadasPage,
      ),
  },

  // Phase 18 v1.4 — KYC-02 admin queue + detail. `adminGuard` mirrors the
  // server-side DB-checked AdminGuard; non-admins bounce to `/` (which then
  // resolves to their normal shell). UX hint only — the API enforces.
  {
    path: 'admin/kyc',
    canActivate: [authGuard, adminGuard],
    loadComponent: () =>
      import('./features/admin/kyc-list.page').then((m) => m.AdminKycListPage),
  },
  {
    path: 'admin/kyc/:id',
    canActivate: [authGuard, adminGuard],
    loadComponent: () =>
      import('./features/admin/kyc-detail.page').then(
        (m) => m.AdminKycDetailPage,
      ),
  },

  // Phase 33 — SUP-01. User-facing suporte ticket system. authGuard only
  // (both EMPRESA and PROFISSIONAL roles can file tickets).
  {
    path: 'suporte/tickets',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/suporte/lista/suporte-tickets.page').then(
        (m) => m.SuporteTicketsPage,
      ),
  },
  {
    path: 'suporte/tickets/:id',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/suporte/detalhe/suporte-detalhe.page').then(
        (m) => m.SuporteDetalhePage,
      ),
  },

  // Phase 41 v1.9 — ADMIN-AUDIT-01. Admin BulkAuditLog viewer. adminGuard
  // mirrors server-side AdminGuard; non-admins bounce to `/`. The backend
  // GET /admin/audit/bulk is the authoritative gate.
  {
    path: 'admin/audit/bulk',
    canActivate: [authGuard, adminGuard],
    loadComponent: () =>
      import('./features/admin/audit-bulk/audit-bulk.page').then(
        (m) => m.AdminAuditBulkPage,
      ),
  },

  // Phase 33 — SUP-01. Admin suporte ticket management. adminGuard mirrors
  // server-side RolesGuard('admin'). Non-admins bounce to `/`.
  {
    path: 'admin/suporte/tickets',
    canActivate: [authGuard, adminGuard],
    loadComponent: () =>
      import('./features/admin/suporte-lista.page').then(
        (m) => m.AdminSuporteListaPage,
      ),
  },
  {
    path: 'admin/suporte/tickets/:id',
    canActivate: [authGuard, adminGuard],
    loadComponent: () =>
      import('./features/admin/suporte-detalhe.page').then(
        (m) => m.AdminSuporteDetalhePage,
      ),
  },

  // Wildcard fallback
  { path: '**', redirectTo: 'welcome' },
];
